/**
 * Row primitives — the leaf presentational pieces of a product ROW: image link,
 * name link, price, variation lines, the inline actions and the remove button.
 *
 * Not basket-specific, though it was born there (`routes/resources/components/
 * basket-line-item.primitives.tsx` until COM-205): the cart page, the minicart,
 * save-for-later, the favorites list and the telehealth cart row all use it.
 *
 * Leaves only. The stateful, basket-context-bound wrappers stay in
 * `routes/resources/components/basket-line-item.components.tsx`.
 */
import { Trash2 } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { LoadingButton } from "~/components/ui/loading-button";
import type { C_ProductInfo } from "~/lib/app.types";
import { cn } from "~/lib/ui";
import type { VariantRow } from "~/routes/resources/components/basket-line-item.variations";
import { ProductListingImage } from "~/routes/resources/components/product-listings-image";
import { ProductPrice } from "~/shop/components/product-price";
import type { PriceDisplay } from "~/shop/utils/price-display";
import { resolvePriceFromLineItem } from "~/shop/utils/price-display";

export const ProductImageLinkPrimitive = ({
  href,
  product,
  size,
  className,
  "data-testid": dataTestId,
}: {
  href: string;
  product: C_ProductInfo | undefined;
  size?: "xs" | "medium" | "large";
  className?: string;
  "data-testid"?: string;
}) => {
  if (!product) return null;
  return (
    <Link
      prefetch="intent"
      className="text-dark-on-light text-sm font-semibold"
      to={href}
      data-testid={dataTestId}
    >
      <ProductListingImage
        product={product}
        size={size}
        className={className}
      />
    </Link>
  );
};

export const ProductNameLinkPrimitive = ({
  href,
  name,
  className,
  linkClassName,
  "data-testid": dataTestId,
}: {
  href: string;
  name: ReactNode;
  /** Applied to the wrapping `<h3>` — layout/spacing for the row's slot. */
  className?: string;
  /**
   * Applied to the `<Link>` itself. Needed for type overrides: the anchor sets
   * its own `font-medium`, which beats anything inherited from the `<h3>`, so
   * a caller wanting a different weight (the telehealth row's semibold
   * heading) cannot get there via `className`.
   */
  linkClassName?: string;
  "data-testid"?: string;
}) => (
  <h3 className={cn("", className)}>
    <Link
      prefetch="intent"
      className={cn(
        "font-grotesque text-base font-medium text-contrast-black",
        linkClassName,
      )}
      to={href}
      data-testid={dataTestId}
    >
      {name}
    </Link>
  </h3>
);

/**
 * The row's price. Styling only — WHAT to display is the caller's decision, and
 * callers hold different things: a resolved SKU is fully described by
 * `currentPrice`/`salePrice`, while a caller holding a whole product passes a
 * `display` it resolved itself, because a product can price as a range and that
 * pair cannot express one (COM-205).
 *
 * `text-minicart-cost` is historical, not a coupling: per site it resolves to
 * the same color as the tile's `--tile-price-color` (FSA #2458a9, HSA #55449a,
 * WDH #8d3888).
 */
export const ProductPricePrimitive = ({
  currentPrice,
  salePrice,
  display: displayProp,
  className,
  "data-testid": dataTestId,
}: {
  currentPrice?: number | undefined;
  salePrice?: number | undefined;
  display?: PriceDisplay | null;
  className?: string;
  "data-testid"?: string;
}) => {
  const display =
    displayProp ?? resolvePriceFromLineItem({ currentPrice, salePrice });
  const wrapperClassName = cn(
    "font-grotesque text-base font-bold text-minicart-cost md:py-1.5",
    className,
  );
  // Preserve the "—" placeholder for a row with no price at all.
  if (!display)
    return (
      <p className={wrapperClassName} data-testid={dataTestId}>
        —
      </p>
    );
  return (
    <ProductPrice
      display={display}
      data-testid={dataTestId}
      as="p"
      className={wrapperClassName}
      strikeClassName="text-gray-500"
      salePriceClassName="text-sale-price"
    />
  );
};

export const ProductVariantsPrimitive = ({
  variations,
  className,
}: {
  variations: VariantRow[];
  className?: string;
}) => {
  if (variations.length === 0) return null;
  return (
    <div className={cn("space-y-3 py-1.5", className)}>
      {variations.map(({ id, displayName, value }) => (
        <p key={id} className="text-xs font-light text-contrast-black">
          <span className="font-grotesque text-sm font-light">
            {displayName}:{" "}
          </span>
          <span className="font-grotesque text-sm font-medium">{value}</span>
        </p>
      ))}
    </div>
  );
};

export const LineActionsContainerPrimitive = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "mt-2 flex flex-col flex-wrap items-center gap-x-4 gap-y-1 font-grotesque text-xs",
      className,
    )}
  >
    {children}
  </div>
);

/**
 * Shared class string for a line-item action, exported so a non-button action can
 * match a real one instead of re-typing its classes.
 *
 * The telehealth row's "Edit plan" has to be an `<a>` (it navigates) yet must sit
 * flush beside Save-for-later, which is a `<button>`. Sharing this string is what
 * keeps the two matching; duplicating the classes in both places does not.
 *
 * `disabled:opacity-50` is intentionally NOT here: it's meaningless on an anchor,
 * and only the button below needs it.
 */
export const LINE_ACTION_CLASSNAME =
  "cursor-pointer text-start font-grotesque text-base font-medium underline";

export const LineActionButtonPrimitive = ({
  onClick,
  disabled = false,
  className,
  children,
  "data-testid": dataTestId,
}: {
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
  "data-testid"?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={dataTestId}
    className={cn(LINE_ACTION_CLASSNAME, "disabled:opacity-50", className)}
  >
    {children}
  </button>
);

// TODO extract out IconButton with loading state?
export const RemovePrimitive = ({
  onClick,
  isLoading = false,
  ariaLabel,
  className,
  "data-testid": testId,
}: {
  onClick: () => void;
  isLoading?: boolean;
  ariaLabel?: string;
  className?: string;
  "data-testid"?: string;
}) => {
  return (
    <LoadingButton
      isLoading={isLoading}
      variant="ghost"
      className={cn(
        "flex h-6 w-6 cursor-pointer justify-center self-end px-0 hover:no-underline",
        className,
      )}
      aria-label={ariaLabel}
      data-testid={testId}
      onClick={onClick}
      loadingIndicatorHidden={true}
    >
      <Trash2
        stroke={isLoading ? "white" : "black"}
        className="w-4.5 inline h-5"
      />
    </LoadingButton>
  );
};
